import { useEffect, useRef, type ReactNode } from 'react';
import { useFinePointer, usePrefersReducedMotion } from '../lib/motion';
import { PixelSprite } from '../skins/pixel/PixelSprite';
import { STEVE } from '../skins/pixel/sprites';
import { useSteve } from '../state/context';
import styles from './Steve.module.css';

/** Décalage de la cible par rapport au curseur, et force du lissage. */
const OFFSET_X = 24;
const OFFSET_Y = 18;
const EASING = 0.09;
/** Seuil de changement de sens, pour éviter que Steve papillonne sur place. */
const FLIP_THRESHOLD = 0.6;
const MAX_TILT_DEG = 14;

/**
 * Petit Steve qui suit le curseur avec un temps de retard.
 *
 * Le suivi passe entièrement par `element.style.transform` dans une boucle
 * `requestAnimationFrame` : aucun rendu React par image, et donc aucun coût
 * quand le reste de la page travaille. Désactivé au pointeur grossier et sous
 * `prefers-reduced-motion`.
 *
 * Deux skins redessinent Steve au lieu de le reprendre tel quel — le 8 bits et
 * le 3D. Le poisson-curseur les suit, sinon il détonnait : sur le skin pixel,
 * tout était en gros pixels sauf lui. Il emprunte donc le sprite du skin pixel,
 * et un ombrage en dégradés sur le skin 3D. Partout ailleurs, le dessin CSS
 * d'origine.
 */
export function SteveFollower(): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  const fine = useFinePointer();
  const reduced = usePrefersReducedMotion();
  // Le menu Steve du skin paint peut l'éteindre : dessiner avec un poisson qui
  // colle au curseur finit par gêner.
  const { followCursor, skin } = useSteve();
  const enabled = fine && !reduced && followCursor;

  useEffect(() => {
    const node = ref.current;
    if (!enabled || !node) return;

    let targetX = 0;
    let targetY = 0;
    let x = 0;
    let y = 0;
    let flip = 1;
    let seen = false;
    let frame = 0;

    const onMove = (event: MouseEvent): void => {
      targetX = event.clientX + OFFSET_X;
      targetY = event.clientY + OFFSET_Y;
      if (!seen) {
        // Première position connue : on y place Steve sans l'y faire glisser
        // depuis le coin de l'écran.
        seen = true;
        x = targetX;
        y = targetY;
        node.classList.add(styles.followerVisible ?? '');
      }
    };

    const step = (): void => {
      if (seen) {
        const dx = targetX - x;
        const dy = targetY - y;
        x += dx * EASING;
        y += dy * EASING;
        if (dx < -FLIP_THRESHOLD) flip = -1;
        else if (dx > FLIP_THRESHOLD) flip = 1;
        const tilt = Math.max(-MAX_TILT_DEG, Math.min(MAX_TILT_DEG, dy * 0.5));
        node.style.transform = `translate(${String(x)}px, ${String(y)}px) scaleX(${String(flip)}) rotate(${String(tilt)}deg)`;
      }
      frame = window.requestAnimationFrame(step);
    };

    document.addEventListener('mousemove', onMove, { passive: true });
    frame = window.requestAnimationFrame(step);

    return () => {
      document.removeEventListener('mousemove', onMove);
      window.cancelAnimationFrame(frame);
    };
  }, [enabled]);

  if (!enabled) return null;

  /*
   * Le sprite passe par le même canvas que le skin pixel : 34 × 20 pixels
   * réels, agrandis deux fois par le CSS. Pas de second dessin à maintenir, et
   * la queue reste celle du canon.
   */
  if (skin === 'pixel') {
    return (
      <div ref={ref} className={`${styles.follower} ${styles.followerPixel}`} data-follower="pixel" aria-hidden="true">
        <PixelSprite sprite={STEVE} className={styles.followerCanvas} />
      </div>
    );
  }

  /*
   * Même squelette qu'ailleurs : c'est la feuille de style qui met le relief,
   * sous `.followerThreed`. Refaire un second Steve en dur ferait diverger les
   * deux dessins à la première retouche.
   */
  const variant = skin === 'threed' ? ` ${styles.followerThreed ?? ''}` : '';

  return (
    <div ref={ref} className={`${styles.follower}${variant}`} data-follower={skin === 'threed' ? '3d' : 'css'} aria-hidden="true">
      <span className={`${styles.part} ${styles.followerArmBack}`} />
      <span className={`${styles.part} ${styles.followerTail}`} />
      <span className={`${styles.part} ${styles.followerBody}`} />
      <span className={`${styles.part} ${styles.followerEye}`}>
        <span className={styles.followerPupil} />
      </span>
      <span className={`${styles.part} ${styles.followerArmFront}`} />
      <span className={`${styles.part} ${styles.followerLeg} ${styles.followerLegLeft}`} />
      <span className={`${styles.part} ${styles.followerLeg} ${styles.followerLegRight}`} />
    </div>
  );
}
